import type { MarketResponseDto } from "Infrastructure/Api/Api";
import { useImportOrdersCommentMutation } from "Market/Api/Mutation/importOrdersCommentsMutation";
import { useImportOrdersMutation } from "Market/Api/Mutation/importOrdersMutation";
import { useImportOrdersPartnersMutation } from "Market/Api/Mutation/importOrdersPartnersMutation";
import { useImportOrdersPriceMutation } from "Market/Api/Mutation/importOrdersPriceMutation";
import { useImportOrdersRegionsMutation } from "Market/Api/Mutation/importOrdersRegionsMutation";
import { useImportOrdersSourceMutation } from "Market/Api/Mutation/importOrdersSourceMutation";
import {
	FormFileTypes,
	FormUploadButton,
} from "Shared/Components/Form/CustomFormComponent/FormUploadButton";
import { zodResolver } from "@hookform/resolvers/zod";
import { Grid2, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type GeneralTabProps = {
	market: MarketResponseDto;
};

export const MarketOrderImportTab: FC<GeneralTabProps> = ({ market }) => {
	const { mutate: importOrders, isPending: isUploading } =
		useImportOrdersMutation(market.id, 1);
	const { mutate: importOrdersComment, isPending: isUploadingComments } =
		useImportOrdersCommentMutation(market.id, 1);
	const { mutate: importOrdersRegions, isPending: isUploadingRegions } =
		useImportOrdersRegionsMutation(market.id, 1);
	const { mutate: importOrdersPartners, isPending: isUploadingPartners } =
		useImportOrdersPartnersMutation(market.id, 1);

	const { mutate: importOrdersSource, isPending: isUploadingSource } =
		useImportOrdersSourceMutation(market.id, 1);

	const { mutate: importOrdersPrice, isPending: isUploadingPrice } =
		useImportOrdersPriceMutation(market.id, 1);

	const form = useForm({
		resolver: zodResolver(
			z.object({
				content: z
					.instanceof(File)
					.optional()
					.nullable()
					.refine((file) => !file || file?.type === "text/csv", {
						message: "This file type is not compatible",
					}),
			}),
		),
	});

	const { control } = form;

	const handleUpload = (file: File | FileList) => {
		importOrders({
			content: file,
		});
	};

	const handleUploadComments = (file: File | FileList) => {
		importOrdersComment({
			content: file,
		});
	};

	const handleUploadRegions = (file: File | FileList) => {
		importOrdersRegions({
			content: file,
		});
	};

	const handleUploadPartners = (file: File | FileList) => {
		importOrdersPartners({
			content: file,
		});
	};

	const handleUploadSource = (file: File | FileList) => {
		importOrdersSource({
			content: file,
		});
	};

	const handleUploadPrice = (file: File | FileList) => {
		importOrdersPrice({
			content: file,
		});
	};

	return (
		<Grid2 container spacing={2} sx={{ padding: 2 }}>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Upload import</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUpload}
						isLoading={isUploading}
						disabled={isUploading}
						valid={true}
					/>
				</Stack>
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Upload comments</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUploadComments}
						isLoading={isUploadingComments}
						disabled={isUploadingComments}
						valid={true}
					/>
				</Stack>
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Upload regions</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUploadRegions}
						isLoading={isUploadingRegions}
						disabled={isUploadingRegions}
						valid={true}
					/>
				</Stack>
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Upload partners</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUploadPartners}
						isLoading={isUploadingPartners}
						disabled={isUploadingPartners}
						valid={true}
					/>
				</Stack>
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Upload sources</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUploadSource}
						isLoading={isUploadingSource}
						disabled={isUploadingSource}
						valid={true}
					/>
				</Stack>
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
				<Stack alignItems="center">
					<Typography variant="h6">Update current price</Typography>
					<FormUploadButton
						control={control}
						accept={FormFileTypes.csv}
						name="content"
						customOnChange={handleUploadPrice}
						isLoading={isUploadingPrice}
						disabled={isUploadingPrice}
						valid={true}
					/>
				</Stack>
			</Grid2>
		</Grid2>
	);
};
